import React, { useEffect, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import scrollToComponent from 'react-scroll-to-component';

// material ui
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

import ExtendedSearchSmartSearchTab from './ExtendedSearchSmartSearchTab';
import ExtendedSearchCategorySearchTab from './ExtendedSearchCategorySearchTab';
import ExtendedSearchGpsSearchTab from './ExtendedSearchGpsSearchTab';
import ExtendedSearchFaceSearchTab from './ExtendedSearchFaceSearchTab';
import { FormattedMessageWrappedInSpan } from '../../misc';

const SMART_SEARCH = 0;
const CATEGORY_SEARCH = 1;
const GPS_SEARCH = 2;
const FACE_SEARCH = 3;

const ExtendedSearch = (props) => {
  const {
    intl,
    event,
    startnumber,
    selectedTab,
    expand,
    selectedCategoryId,
    hasSmartSearchTab,
    hasCategorySearchTab,
    hasGpsSearchTab,
    hasFaceSearchTab,
    onTabChange,
    onExpand,
  } = props;

  const isSmartSearchPage = props.match.path === '/:language/event/:id/smartsearch/:startnumber';
  const isCategorySearchPage = (
    props.match.path === '/:language/event/:id/category/:categoryId'
    || props.match.path === '/:language/event/:id/category/:categoryId/:startnumber'
  )

  const extendedSearchRef = useRef(null);

  useEffect(() => {
    if(expand) {
      scrollToComponent(extendedSearchRef.current, { align: 'top' });
    }
  }, [expand]);

  const handleNavigateTo = (url) => {
    if (props.match.path === '/:language/event/:id') {
      props.history.push(url);
    } else {
      props.history.replace(url);
    }
  };

  return (
    <>
      {!expand && (
        <div className="row">
          <div className="col-xs-16 col-sm-12 col-sm-offset-2">
            <div
              className={classNames('extended-search-expand', {
                'extended-search-category-page':
                  isCategorySearchPage && selectedTab === 1 && expand,
              })}
              onClick={onExpand}
            >
              <div className="extended-search-expand-desktop">
                <FormattedMessageWrappedInSpan
                  id="extendedSearch.morePhotosDesktop"
                  defaultMessage="+ Are there more photos of me?"
                />
              </div>
              <div className="extended-search-expand-mobile">
                <FormattedMessageWrappedInSpan
                  id="extendedSearch.morePhotosMobile"
                  defaultMessage="More?"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {expand && (
        <div className="row" ref={extendedSearchRef}>
          <div className="col-xs-16 col-sm-12 col-sm-offset-2">
            <div>
              <Tabs
                classes={{ indicator: 'extended-search-tab-indicator' }}
                onChange={onTabChange}
                value={selectedTab}
                variant="fullWidth"
              >
                {hasFaceSearchTab && (
                  <Tab
                    classes={{ root: 'extended-search-tab' }}
                    label={intl.formatMessage(translations.faceSearchTabTitle)}
                    value={FACE_SEARCH}
                  />
                )}
                {hasSmartSearchTab && (
                  <Tab
                    classes={{ root: 'extended-search-tab' }}
                    label={intl.formatMessage(translations.smartSearchTabTitle)}
                    value={SMART_SEARCH}
                  />
                )}
                {hasCategorySearchTab && (
                  <Tab
                    classes={{ root: 'extended-search-tab' }}
                    label={intl.formatMessage(translations.categorySearchTabTitle)}
                    value={CATEGORY_SEARCH}
                  />
                )}
                {hasGpsSearchTab && (
                  <Tab
                    classes={{ root: 'extended-search-tab' }}
                    label={intl.formatMessage(translations.gpsSearchTabTitle)}
                    value={GPS_SEARCH}
                  />
                )}
              </Tabs>
              <div className="extended-search-tab-content">
                {selectedTab === SMART_SEARCH && (
                  <ExtendedSearchSmartSearchTab
                    eventId={event.sgId}
                    startnumber={startnumber}
                    isSmartSearchPage={isSmartSearchPage}
                    eventHasSearchByFace={event.searchByFace ? true : false}
                  />
                )}
                {selectedTab === CATEGORY_SEARCH && (
                  <ExtendedSearchCategorySearchTab
                    eventId={event.sgId}
                    startnumber={startnumber}
                    categories={event}
                    selectedCategoryId={selectedCategoryId}
                    onNavigateTo={handleNavigateTo}
                  />
                )}
                {selectedTab === GPS_SEARCH && (
                  <ExtendedSearchGpsSearchTab
                    eventId={event.sgId}
                    startnumber={startnumber}
                    onNavigateTo={handleNavigateTo}
                  />
                )}
                {selectedTab === FACE_SEARCH && (
                  <ExtendedSearchFaceSearchTab
                    eventId={event.sgId}
                    startnumber={startnumber}
                    onNavigateTo={handleNavigateTo}
                  />
                )}
              </div>
              <div
                className={classNames('extended-search-expand', {
                  'extended-search-category-page':
                    isCategorySearchPage && selectedTab === 1,
                })}
                onClick={onExpand}
              >
                <div className="extended-search-expand-desktop">
                  <FormattedMessageWrappedInSpan
                    id="extendedSearch.morePhotosDesktop"
                    defaultMessage="+ Are there more photos of me?"
                  />
                </div>
                <div className="extended-search-expand-mobile">
                  <FormattedMessageWrappedInSpan
                    id="extendedSearch.morePhotosMobile"
                    defaultMessage="More?"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const translations = defineMessages({
  smartSearchTabTitle: {
    id: 'extendedSearch.smartSearch.tabTitle',
    defaultMessage: 'Smart Search',
  },
  categorySearchTabTitle: {
    id: 'extendedSearch.searchAfterCategory.tabTitle',
    defaultMessage: 'Categories',
  },
  gpsSearchTabTitle: {
    id: 'extendedSearch.gpsSearch.tabTitle',
    defaultMessage: 'GPS Search',
  },
  faceSearchTabTitle: {
    id: 'extendedSearch.faceSearch.tabTitle',
    defaultMessage: 'Search by Face',
  },
});

ExtendedSearch.defaultProps = {
  hasSmartSearchTab: true,
  hasCategorySearchTab: true,
  hasGpsSearchTab: false,
  hasFaceSearchTab: false,
};

ExtendedSearch.propTypes = {
  event: PropTypes.object,
  startnumber: PropTypes.string,
  selectedTab: PropTypes.number,
  expand: PropTypes.bool,
  selectedCategoryId: PropTypes.string,
  hasSmartSearchTab: PropTypes.bool,
  hasCategorySearchTab: PropTypes.bool,
  hasGpsSearchTab: PropTypes.bool,
  hasFaceSearchTab: PropTypes.bool,
  onTabChange: PropTypes.func,
  onExpand: PropTypes.func,
  match: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.object,
};

export { ExtendedSearch };

export default createFragmentContainer(injectIntl(withRouter(ExtendedSearch)), {
  event: graphql`
    fragment ExtendedSearch_event on Event {
      id
      sgId
      searchByFace
      searchByFaceAsFeature
      ...ExtendedSearchCategorySearchTab_categories
    }
  `,
});
